/**
 * Responsibility: the one Firebase app instance, and the token every API call
 * carries.
 *
 * **The config here is not a secret.** A Firebase web config is downloaded by
 * every browser that loads the page; it identifies the project, it does not
 * authorise anything. What stops a stranger using it is that the hub verifies
 * the token's signature and checks the address against an allowlist it holds.
 * So these live in `VITE_` variables for convenience across environments, not
 * for secrecy, and committing them would be harmless rather than a mistake.
 *
 * `idToken` is a plain function rather than a hook because `client.ts` is not a
 * component and cannot use context. Firebase keeps the current user in a
 * singleton and refreshes an expiring token on demand, so asking it per request
 * is cheap and always current - which is the whole reason not to cache a token
 * ourselves and have to work out when it went stale.
 */
import { initializeApp, type FirebaseOptions } from 'firebase/app';
import {
  browserLocalPersistence,
  getAuth,
  GoogleAuthProvider,
  setPersistence,
  reauthenticateWithPopup,
  signInWithPopup,
  signOut as firebaseSignOut,
  type User,
} from 'firebase/auth';

const config: FirebaseOptions = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

/**
 * Whether there is a project to sign in to at all.
 *
 * A build with no config is a real state - it is what a fresh checkout and CI
 * both look like - and it should say so rather than throwing somewhere deep in
 * the SDK with a message about an invalid API key.
 */
export const firebaseConfigured = Boolean(config.apiKey && config.projectId);

const app = firebaseConfigured ? initializeApp(config) : null;

export const auth = app ? getAuth(app) : null;

if (auth) {
  // Survive a reload. Without this a refresh signs you out, which on a phone
  // means signing in again every time the tab is reclaimed.
  void setPersistence(auth, browserLocalPersistence);
}

/** Open Google's sign-in and resolve when it completes. */
export async function signIn(): Promise<void> {
  if (!auth) throw new Error('Firebase is not configured in this build');
  const provider = new GoogleAuthProvider();
  // Always ask which account. Several of these are shared machines and a
  // silent sign-in as the last person is worse than one extra click.
  provider.setCustomParameters({ prompt: 'select_account' });
  await signInWithPopup(auth, provider);
}

/**
 * Prove it is the same person, right now.
 *
 * **Not the same as being signed in.** A session lasts as long as somebody
 * stays signed in, which for a browser left open on a shared machine is
 * indefinitely. Re-authenticating moves the token's `auth_time`, which the hub
 * checks before it will show or change a credential.
 *
 * The forced refresh afterwards is the part that is easy to miss: the cached
 * token still carries the *old* `auth_time`, so without it the very next
 * request would be refused for staleness despite the popup having just
 * succeeded.
 */
export async function reauthenticate(): Promise<void> {
  if (!auth?.currentUser) throw new Error('Not signed in');
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  try {
    await reauthenticateWithPopup(auth.currentUser, provider);
  } catch (error) {
    // A blocked popup is the one failure here that looks like nothing
    // happening, so it gets its own sentence rather than an SDK error code.
    const code = (error as { code?: string })?.code ?? '';
    if (code.includes('popup-blocked')) {
      throw new Error(
        'Your browser blocked the sign-in popup. Allow popups for this site and try again.',
      );
    }
    if (code.includes('popup-closed') || code.includes('cancelled-popup')) {
      throw new Error('Sign-in was cancelled, so nothing was changed.');
    }
    throw error;
  }
  await auth.currentUser.getIdToken(true);
}

/**
 * Seconds since this session last actually signed in.
 *
 * `auth_time` only moves on a real sign-in - refreshing an ID token does not
 * touch it - which is exactly why the hub checks it and why a token refresh
 * can never satisfy a request for a fresh one.
 *
 * Read from the cached token, so this does not hit the network and can be
 * called while a click is still in progress.
 */
export async function secondsSinceSignIn(): Promise<number> {
  if (!auth?.currentUser) return Number.POSITIVE_INFINITY;
  const result = await auth.currentUser.getIdTokenResult();
  const signedInAt = Date.parse(result.authTime);
  if (Number.isNaN(signedInAt)) return Number.POSITIVE_INFINITY;
  return (Date.now() - signedInAt) / 1000;
}

export async function signOut(): Promise<void> {
  if (auth) await firebaseSignOut(auth);
}

/**
 * The current ID token, or null when nobody is signed in.
 *
 * Firebase refreshes it when it is close to expiry, so this is a cheap read
 * almost every time and a network round trip rarely.
 */
export async function idToken(): Promise<string | null> {
  const user: User | null = auth?.currentUser ?? null;
  if (!user) return null;
  return user.getIdToken();
}

/**
 * A token minted now, ignoring the cached one.
 *
 * For the single retry after a 401. Firebase renews close to expiry on its
 * own, so reaching for this means the cached token was rejected and asking
 * again for the same string would fail the same way.
 */
export async function freshToken(): Promise<string | null> {
  const user: User | null = auth?.currentUser ?? null;
  if (!user) return null;
  return user.getIdToken(true);
}
